// Fragment "consult telehealth" — Payment Gateway v1, Flow C. Practitioner-facing
// `consult.*` keys (plus the `tele.*` hints that link the telehealth page to the booking
// catalogue), per contract §9's language-law binding: `ConsultRateSettings`,
// `SessionTimeline`, `ConsultCompleteDialog`, `ConsultTimelineDialog`, `ConsultRoomPage`
// and `TelehealthAdmin`'s consult list all render through `useT("admin")`.
//
// `consult.list.status.*` is a KEY FAMILY, not a handful of labels: its members are
// `modules/gateway/schemas.py` `SESSION_STATES`, and `i18n/__tests__/keyCoverage.test.js`
// reads that tuple out of the Python source, so a state added there goes red here until
// all three languages have a label. Never prune a member because no fixture uses it.
export default {
  fr: {
    "consult.rate.title": "Tarif à la minute",
    "consult.rate.subtitle": "Utilisé pour les téléconsultations facturées à la minute.",
    "consult.rate.loading": "Chargement du tarif…",
    "consult.rate.loadError": "Impossible de charger le tarif pour le moment.",
    "consult.rate.retry": "Réessayer",
    "consult.rate.pendingBanner": "Une proposition à {rate}/min (plus des frais de base de {base}) est en attente d'approbation.",
    "consult.rate.pendingBannerCurrent": "Le tarif actuellement en vigueur reste {rate}/min ({min}–{max} min, frais de base {base}).",
    "consult.rate.pendingBannerNoCurrent": "Aucun tarif n'était en vigueur avant cette proposition.",
    "consult.rate.ratePerMinute": "Tarif par minute",
    "consult.rate.baseFee": "Frais de base (facturés une fois)",
    "consult.rate.minMinutes": "Minutes minimum facturées",
    "consult.rate.maxMinutes": "Minutes maximum facturées (plafond)",
    "consult.rate.instrumentsNote": "Le plancher garantit qu'une durée minimale est payée ; il s'estompe à mesure que l'appel se prolonge au-delà. Les frais de base tarifient l'ouverture de la consultation et sont facturés intégralement à chaque fois — ils ne s'estompent jamais.",
    "consult.rate.ceilingPreviewLabel": "Ce tarif autorise jusqu'à",
    "consult.rate.ceilingPreviewValue": "{amount}",
    "consult.rate.outOfBandNote": "Ce tarif sort de la fourchette habituelle ({min}–{max}) : il sera envoyé à Solunary pour approbation, et votre tarif actuel reste actif en attendant.",
    "consult.rate.save": "Enregistrer",
    "consult.rate.saved": "Tarif enregistré.",
    "consult.rate.sentForApproval": "Tarif envoyé pour approbation — votre tarif actuel reste actif en attendant.",
    "consult.rate.platformPoliciesTitle": "Politiques de la plateforme (lecture seule)",
    "consult.rate.noShowFeeLabel": "Frais d'absence (montant fixe)",
    "consult.rate.rescheduleNoticeLabel": "Délai minimum pour reporter sans frais",
    "consult.rate.rescheduleNoticeValue": "{hours} h avant le rendez-vous",

    "consult.timeline.noSession": "Aucune séance à afficher.",
    "consult.timeline.noEvents": "Aucun événement enregistré.",
    "consult.timeline.rawDuration": "Durée brute : {duration}",
    "consult.timeline.notComputedYet": "Durée pas encore calculée — la consultation est en cours.",
    "consult.timeline.billableMinutes": "Minutes facturables : {minutes}",
    "consult.timeline.gracePeriod": "Délai de grâce appliqué : {seconds} s",
    "consult.timeline.bridgedGap": "Coupure comblée",
    "consult.timeline.evidence.server_events": "Preuve : événements serveur",
    "consult.timeline.evidence.join_and_heartbeat": "Preuve : connexion + pulsations",
    "consult.timeline.evidence.declared": "Preuve : déclarée",
    "consult.timeline.evidence.unknown": "Preuve : inconnue",
    "consult.timeline.event.room_opened": "Salle ouverte",
    "consult.timeline.event.participant_joined": "Connexion",
    "consult.timeline.event.participant_left": "Déconnexion",
    "consult.timeline.event.practitioner_ended": "Le praticien a terminé la séance",
    "consult.timeline.event.meeting_started": "Séance démarrée",
    "consult.timeline.event.meeting_ended": "Séance terminée",
    "consult.timeline.event.gap_detected": "Coupure détectée",
    "consult.timeline.event.auto_completed": "Clôture automatique",
    "consult.timeline.event.rejected_event": "Événement rejeté (hors délai)",
    "consult.timeline.role.practitioner": "praticien",
    "consult.timeline.role.patient": "patient",

    "consult.complete.title": "Clôturer la téléconsultation",
    "consult.complete.desc": "Les minutes facturées sont calculées par le serveur à partir des connexions et déconnexions enregistrées — elles ne se saisissent pas.",
    "consult.complete.actionCancel": "Annuler",
    // F-06 — this consultation has no session (nobody opened the room), so there are no
    // minutes to bill and no no-show to record; the card hold may still be live, and
    // "Cancel" is what releases it.
    "consult.complete.neverOpened": "Personne n'est jamais entré dans cette salle : il n'y a aucune minute à facturer. « Annuler » libère l'empreinte bancaire du patient.",
    "consult.complete.actionNoShow": "Absence",
    "consult.complete.actionComplete": "Clôturer",
    "consult.complete.noShowWho": "Qui ne s'est pas présenté ?",
    "consult.complete.noShowPatient": "Le patient",
    "consult.complete.noShowPractitioner": "Le praticien",
    "consult.complete.back": "Retour",
    "consult.complete.noShowConfirm": "Confirmer l'absence",
    "consult.complete.billableMinutes": "Minutes facturées",
    "consult.complete.gross": "Montant brut",
    "consult.complete.commission": "Commission",
    "consult.complete.net": "Net au praticien",
    "consult.complete.released": "Montant libéré",
    "consult.complete.done": "Fermer",
    "consult.complete.cancelled": "Rendez-vous annulé.",

    "consult.list.title": "Vos consultations",
    "consult.list.loading": "Chargement des consultations…",
    "consult.list.loadError": "Impossible de charger vos consultations pour le moment.",
    "consult.list.retry": "Réessayer",
    "consult.list.tab.upcoming": "À venir",
    "consult.list.tab.inProgress": "En cours",
    "consult.list.tab.awaitingCompletion": "À clôturer",
    "consult.list.tab.done": "Terminées",
    "consult.list.empty.upcoming": "Aucune consultation à venir.",
    "consult.list.empty.inProgress": "Aucune consultation en cours.",
    "consult.list.empty.awaitingCompletion": "Aucune consultation à clôturer.",
    "consult.list.empty.done": "Aucune consultation terminée pour l'instant.",
    "consult.list.status.scheduled": "Planifiée",
    "consult.list.status.in_progress": "En cours",
    "consult.list.status.computing": "Calcul des minutes",
    "consult.list.status.computed": "Minutes calculées",
    "consult.list.status.captured": "Encaissée",
    "consult.list.status.void_no_show": "Absence",
    "consult.list.status.cancelled": "Annulée",
    // F-06 — the state of a consultation whose room was NEVER opened: there is no session
    // yet, so the pill states the MONEY, the only thing that has happened to it. The
    // family mirrors `schemas.CONSULT_AUTH_STATES`.
    "consult.list.auth.none": "Non autorisée",
    "consult.list.auth.awaiting": "Paiement en attente",
    "consult.list.auth.authorised": "Autorisée · salle jamais ouverte",
    "consult.list.auth.failed": "Autorisation refusée",
    "consult.list.openRoom": "Ouvrir la salle",
    "consult.list.complete": "Clôturer",
    "consult.list.timeline": "Historique",
    "consult.list.billed": "{minutes} min · {amount}",
    "consult.list.billedPending": "{minutes} min mesurées · montant en attente de la capture du paiement.",
    "consult.list.resendReceipt": "Renvoyer le reçu",
    "consult.list.receiptResent": "Reçu renvoyé à {email}.",
    "consult.list.receiptResendError": "Échec de l'envoi du reçu.",

    "consult.timeline.dialogTitle": "Historique de la consultation",

    "consult.room.admin.end": "Terminer la consultation",
    "consult.room.admin.meterOnCompletion": "Les minutes facturées sont mesurées par le serveur ; elles s'affichent une fois la consultation terminée.",
    "consult.room.admin.fixedBlock": "Séance à durée fixe, déjà réglée à la réservation. La salle se ferme à la fin du créneau.",
    "consult.room.admin.billed": "Facturé : {minutes} min · {amount}",
    "consult.room.admin.billedPending": "{minutes} min mesurées · montant en attente de la capture du paiement.",
    "consult.room.admin.capReached": "Le plafond autorisé est atteint — le compteur s'est arrêté, pas l'appel.",
    "consult.room.admin.endingSoon": "Se termine à {when}",
    "consult.room.admin.overrun": "Temps réservé dépassé — fin prévue à {when}",
    "consult.room.admin.solo": "Une seule personne dans la salle — déconnexion dans {minutes} min",
    "consult.room.admin.roomClosed": "Salle fermée",

    "tele.noVideoService": "Aucun service n'est proposé en visioconsultation : personne ne peut encore réserver de téléconsultation.",
    "tele.noVideoServiceLink": "Créer un service en visioconsultation",
  },
  en: {
    "consult.rate.title": "Per-minute rate",
    "consult.rate.subtitle": "Used for telehealth consults billed by the minute.",
    "consult.rate.loading": "Loading rate…",
    "consult.rate.loadError": "Could not load the rate right now.",
    "consult.rate.retry": "Retry",
    "consult.rate.pendingBanner": "A proposal at {rate}/min (plus a {base} base fee) is awaiting approval.",
    "consult.rate.pendingBannerCurrent": "The currently active rate stays {rate}/min ({min}–{max} min, base fee {base}).",
    "consult.rate.pendingBannerNoCurrent": "No rate was active before this proposal.",
    "consult.rate.ratePerMinute": "Rate per minute",
    "consult.rate.baseFee": "Base fee (charged once)",
    "consult.rate.minMinutes": "Minimum billed minutes",
    "consult.rate.maxMinutes": "Maximum billed minutes (cap)",
    "consult.rate.instrumentsNote": "The floor guarantees a minimum duration is paid for, and it is absorbed into the total as the call runs longer than it. The base fee prices opening the consultation and is charged in full every time — it is never absorbed.",
    "consult.rate.ceilingPreviewLabel": "This rate authorises up to",
    "consult.rate.ceilingPreviewValue": "{amount}",
    "consult.rate.outOfBandNote": "This rate is outside the usual band ({min}–{max}) — it will be sent to Solunary for approval, and your current rate stays active in the meantime.",
    "consult.rate.save": "Save",
    "consult.rate.saved": "Rate saved.",
    "consult.rate.sentForApproval": "Rate sent for approval — your current rate stays active in the meantime.",
    "consult.rate.platformPoliciesTitle": "Platform policies (read-only)",
    "consult.rate.noShowFeeLabel": "No-show fee (flat amount)",
    "consult.rate.rescheduleNoticeLabel": "Minimum notice to reschedule for free",
    "consult.rate.rescheduleNoticeValue": "{hours} h before the appointment",

    "consult.timeline.noSession": "No session to show.",
    "consult.timeline.noEvents": "No events recorded.",
    "consult.timeline.rawDuration": "Raw duration: {duration}",
    "consult.timeline.notComputedYet": "Duration not computed yet — the consultation is still running.",
    "consult.timeline.billableMinutes": "Billable minutes: {minutes}",
    "consult.timeline.gracePeriod": "Grace period applied: {seconds}s",
    "consult.timeline.bridgedGap": "Bridged gap",
    "consult.timeline.evidence.server_events": "Evidence: server events",
    "consult.timeline.evidence.join_and_heartbeat": "Evidence: join + heartbeats",
    "consult.timeline.evidence.declared": "Evidence: declared",
    "consult.timeline.evidence.unknown": "Evidence: unknown",
    "consult.timeline.event.room_opened": "Room opened",
    "consult.timeline.event.participant_joined": "Joined",
    "consult.timeline.event.participant_left": "Left",
    "consult.timeline.event.practitioner_ended": "Practitioner ended the session",
    "consult.timeline.event.meeting_started": "Meeting started",
    "consult.timeline.event.meeting_ended": "Meeting ended",
    "consult.timeline.event.gap_detected": "Gap detected",
    "consult.timeline.event.auto_completed": "Auto-completed",
    "consult.timeline.event.rejected_event": "Event rejected (out of window)",
    "consult.timeline.role.practitioner": "practitioner",
    "consult.timeline.role.patient": "patient",

    "consult.complete.title": "Complete the telehealth consult",
    "consult.complete.desc": "Billed minutes are computed by the server from the recorded joins and leaves — they are never typed in.",
    "consult.complete.actionCancel": "Cancel",
    "consult.complete.neverOpened": "Nobody ever entered this room, so there are no minutes to bill. \u201cCancel\u201d releases the hold on the patient\u2019s card.",
    "consult.complete.actionNoShow": "No-show",
    "consult.complete.actionComplete": "Complete",
    "consult.complete.noShowWho": "Who didn't show up?",
    "consult.complete.noShowPatient": "The patient",
    "consult.complete.noShowPractitioner": "The practitioner",
    "consult.complete.back": "Back",
    "consult.complete.noShowConfirm": "Confirm no-show",
    "consult.complete.billableMinutes": "Billed minutes",
    "consult.complete.gross": "Gross amount",
    "consult.complete.commission": "Commission",
    "consult.complete.net": "Net to practitioner",
    "consult.complete.released": "Amount released",
    "consult.complete.done": "Close",
    "consult.complete.cancelled": "Appointment cancelled.",

    "consult.list.title": "Your consultations",
    "consult.list.loading": "Loading consultations…",
    "consult.list.loadError": "Could not load your consultations right now.",
    "consult.list.retry": "Retry",
    "consult.list.tab.upcoming": "Upcoming",
    "consult.list.tab.inProgress": "In progress",
    "consult.list.tab.awaitingCompletion": "To close out",
    "consult.list.tab.done": "Done",
    "consult.list.empty.upcoming": "No upcoming consultation.",
    "consult.list.empty.inProgress": "No consultation in progress.",
    "consult.list.empty.awaitingCompletion": "No consultation waiting to be closed out.",
    "consult.list.empty.done": "No finished consultation yet.",
    "consult.list.status.scheduled": "Scheduled",
    "consult.list.status.in_progress": "In progress",
    "consult.list.status.computing": "Computing minutes",
    "consult.list.status.computed": "Minutes computed",
    "consult.list.status.captured": "Captured",
    "consult.list.status.void_no_show": "No-show",
    "consult.list.status.cancelled": "Cancelled",
    "consult.list.auth.none": "Not authorised",
    "consult.list.auth.awaiting": "Awaiting payment",
    "consult.list.auth.authorised": "Authorised · room never opened",
    "consult.list.auth.failed": "Authorisation declined",
    "consult.list.openRoom": "Open room",
    "consult.list.complete": "Close out",
    "consult.list.timeline": "Timeline",
    "consult.list.billed": "{minutes} min · {amount}",
    "consult.list.billedPending": "{minutes} min measured · amount pending the payment capture.",
    "consult.list.resendReceipt": "Resend receipt",
    "consult.list.receiptResent": "Receipt resent to {email}.",
    "consult.list.receiptResendError": "Failed to resend the receipt.",

    "consult.timeline.dialogTitle": "Consultation timeline",

    "consult.room.admin.end": "End consultation",
    "consult.room.admin.meterOnCompletion": "Billed minutes are measured by the server; they appear once you end the consultation.",
    "consult.room.admin.fixedBlock": "Fixed-length session, already paid at booking. The room closes at the end of the slot.",
    "consult.room.admin.billed": "Billed: {minutes} min · {amount}",
    "consult.room.admin.billedPending": "{minutes} min measured · amount pending the payment capture.",
    "consult.room.admin.capReached": "The authorised ceiling has been reached — the meter has stopped, the call has not.",
    "consult.room.admin.endingSoon": "Ends at {when}",
    "consult.room.admin.overrun": "Past the booked end of {when}",
    "consult.room.admin.solo": "Only one person in the room — disconnected in {minutes} min",
    "consult.room.admin.roomClosed": "Room closed",

    "tele.noVideoService": "No service is delivered by video, so nobody can book a consultation yet.",
    "tele.noVideoServiceLink": "Create a video service",
  },
  es: {
    "consult.rate.title": "Tarifa por minuto",
    "consult.rate.subtitle": "Se usa para teleconsultas facturadas por minuto.",
    "consult.rate.loading": "Cargando tarifa…",
    "consult.rate.loadError": "No se pudo cargar la tarifa en este momento.",
    "consult.rate.retry": "Reintentar",
    "consult.rate.pendingBanner": "Una propuesta de {rate}/min (más una tarifa base de {base}) está pendiente de aprobación.",
    "consult.rate.pendingBannerCurrent": "La tarifa actualmente vigente sigue en {rate}/min ({min}–{max} min, tarifa base {base}).",
    "consult.rate.pendingBannerNoCurrent": "No había ninguna tarifa vigente antes de esta propuesta.",
    "consult.rate.ratePerMinute": "Tarifa por minuto",
    "consult.rate.baseFee": "Tarifa base (se cobra una sola vez)",
    "consult.rate.minMinutes": "Minutos mínimos facturados",
    "consult.rate.maxMinutes": "Minutos máximos facturados (tope)",
    "consult.rate.instrumentsNote": "El mínimo garantiza que se pague una duración mínima, y se absorbe en el total a medida que la llamada se alarga más allá de él. La tarifa base cobra por abrir la consulta y se cobra íntegramente cada vez — nunca se absorbe.",
    "consult.rate.ceilingPreviewLabel": "Esta tarifa autoriza hasta",
    "consult.rate.ceilingPreviewValue": "{amount}",
    "consult.rate.outOfBandNote": "Esta tarifa está fuera del rango habitual ({min}–{max}): se enviará a Solunary para su aprobación, y su tarifa actual sigue activa mientras tanto.",
    "consult.rate.save": "Guardar",
    "consult.rate.saved": "Tarifa guardada.",
    "consult.rate.sentForApproval": "Tarifa enviada para aprobación — su tarifa actual sigue activa mientras tanto.",
    "consult.rate.platformPoliciesTitle": "Políticas de la plataforma (solo lectura)",
    "consult.rate.noShowFeeLabel": "Cargo por ausencia (monto fijo)",
    "consult.rate.rescheduleNoticeLabel": "Aviso mínimo para reprogramar sin costo",
    "consult.rate.rescheduleNoticeValue": "{hours} h antes de la cita",

    "consult.timeline.noSession": "No hay ninguna sesión para mostrar.",
    "consult.timeline.noEvents": "No se registraron eventos.",
    "consult.timeline.rawDuration": "Duración bruta: {duration}",
    "consult.timeline.notComputedYet": "Duración aún no calculada — la consulta sigue en curso.",
    "consult.timeline.billableMinutes": "Minutos facturables: {minutes}",
    "consult.timeline.gracePeriod": "Periodo de gracia aplicado: {seconds} s",
    "consult.timeline.bridgedGap": "Corte cubierto",
    "consult.timeline.evidence.server_events": "Evidencia: eventos del servidor",
    "consult.timeline.evidence.join_and_heartbeat": "Evidencia: conexión + pulsos",
    "consult.timeline.evidence.declared": "Evidencia: declarada",
    "consult.timeline.evidence.unknown": "Evidencia: desconocida",
    "consult.timeline.event.room_opened": "Sala abierta",
    "consult.timeline.event.participant_joined": "Conexión",
    "consult.timeline.event.participant_left": "Desconexión",
    "consult.timeline.event.practitioner_ended": "El profesional terminó la sesión",
    "consult.timeline.event.meeting_started": "Sesión iniciada",
    "consult.timeline.event.meeting_ended": "Sesión terminada",
    "consult.timeline.event.gap_detected": "Corte detectado",
    "consult.timeline.event.auto_completed": "Cierre automático",
    "consult.timeline.event.rejected_event": "Evento rechazado (fuera de plazo)",
    "consult.timeline.role.practitioner": "profesional",
    "consult.timeline.role.patient": "paciente",

    "consult.complete.title": "Cerrar la teleconsulta",
    "consult.complete.desc": "Los minutos facturados los calcula el servidor a partir de las conexiones y desconexiones registradas — nunca se escriben a mano.",
    "consult.complete.actionCancel": "Cancelar",
    "consult.complete.neverOpened": "Nadie entr\u00f3 nunca en esta sala, as\u00ed que no hay minutos que facturar. \u201cCancelar\u201d libera la retenci\u00f3n en la tarjeta del paciente.",
    "consult.complete.actionNoShow": "Ausencia",
    "consult.complete.actionComplete": "Cerrar",
    "consult.complete.noShowWho": "¿Quién no se presentó?",
    "consult.complete.noShowPatient": "El paciente",
    "consult.complete.noShowPractitioner": "El profesional",
    "consult.complete.back": "Volver",
    "consult.complete.noShowConfirm": "Confirmar ausencia",
    "consult.complete.billableMinutes": "Minutos facturados",
    "consult.complete.gross": "Monto bruto",
    "consult.complete.commission": "Comisión",
    "consult.complete.net": "Neto para el profesional",
    "consult.complete.released": "Monto liberado",
    "consult.complete.done": "Cerrar",
    "consult.complete.cancelled": "Cita cancelada.",

    "consult.list.title": "Sus consultas",
    "consult.list.loading": "Cargando consultas…",
    "consult.list.loadError": "No se pudieron cargar sus consultas en este momento.",
    "consult.list.retry": "Reintentar",
    "consult.list.tab.upcoming": "Próximas",
    "consult.list.tab.inProgress": "En curso",
    "consult.list.tab.awaitingCompletion": "Por cerrar",
    "consult.list.tab.done": "Finalizadas",
    "consult.list.empty.upcoming": "No hay consultas próximas.",
    "consult.list.empty.inProgress": "No hay ninguna consulta en curso.",
    "consult.list.empty.awaitingCompletion": "No hay ninguna consulta por cerrar.",
    "consult.list.empty.done": "Aún no hay consultas finalizadas.",
    "consult.list.status.scheduled": "Programada",
    "consult.list.status.in_progress": "En curso",
    "consult.list.status.computing": "Calculando minutos",
    "consult.list.status.computed": "Minutos calculados",
    "consult.list.status.captured": "Cobrada",
    "consult.list.status.void_no_show": "Ausencia",
    "consult.list.status.cancelled": "Cancelada",
    "consult.list.auth.none": "Sin autorizar",
    "consult.list.auth.awaiting": "Pago pendiente",
    "consult.list.auth.authorised": "Autorizada · sala nunca abierta",
    "consult.list.auth.failed": "Autorización rechazada",
    "consult.list.openRoom": "Abrir sala",
    "consult.list.complete": "Cerrar",
    "consult.list.timeline": "Historial",
    "consult.list.billed": "{minutes} min · {amount}",
    "consult.list.billedPending": "{minutes} min medidos · importe pendiente del cobro del pago.",
    "consult.list.resendReceipt": "Reenviar el recibo",
    "consult.list.receiptResent": "Recibo reenviado a {email}.",
    "consult.list.receiptResendError": "No se pudo reenviar el recibo.",

    "consult.timeline.dialogTitle": "Historial de la consulta",

    "consult.room.admin.end": "Terminar la consulta",
    "consult.room.admin.meterOnCompletion": "Los minutos facturados los mide el servidor; se muestran cuando termina la consulta.",
    "consult.room.admin.fixedBlock": "Sesión de duración fija, ya pagada al reservar. La sala se cierra al terminar la franja.",
    "consult.room.admin.billed": "Facturado: {minutes} min · {amount}",
    "consult.room.admin.billedPending": "{minutes} min medidos · importe pendiente del cobro del pago.",
    "consult.room.admin.capReached": "Se alcanzó el tope autorizado — el contador se detuvo, la llamada no.",
    "consult.room.admin.endingSoon": "Termina a las {when}",
    "consult.room.admin.overrun": "Fuera del tiempo reservado — fin previsto a las {when}",
    "consult.room.admin.solo": "Una sola persona en la sala — desconexión en {minutes} min",
    "consult.room.admin.roomClosed": "Sala cerrada",

    "tele.noVideoService": "Ningún servicio se ofrece por vídeo, así que todavía nadie puede reservar una consulta.",
    "tele.noVideoServiceLink": "Crear un servicio por vídeo",
  },
};
